import React, { useState, useCallback, useEffect } from 'react';
import {
  Col,
  Row,
  Table,
  Select,
  DatePicker,
  TablePaginationConfig,
  Modal,
  Input,
  Tag,
  Popconfirm,
  Card,
  List,
  Avatar,
  Badge,
  Drawer,
  Alert,
  Checkbox,
  InputNumber,
  Space,
  Form,
} from 'antd';
import { SuppliersApi } from 'api/api';
import { ProjectDto, SupplierPaymentDto, PaymentMethod, PaymentHistoryEntry } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { Cards } from 'components/cards/frame/cards-frame';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper, TopToolBox } from 'container/styled';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { ExportButtonPageHeader } from 'components/buttons/export-button/export-button';
import { CustomTableStyleWrapper } from 'pages/style';
import { formatDate, formatCurrency, toCamelCase } from 'utility/utility';
import { SupplierDto } from 'api/models';
import { ProjectsApi } from 'api/api';
import { UnitCategoryDto } from 'api/models';
import { UnitCategoriesApi } from 'api/api';
import dayjs from 'dayjs';
import openNotificationWithIcon from 'utility/notification';
import { hasPermission } from 'utility/accessibility/hasPermission';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { PlusOutlined, EditOutlined, FilterOutlined } from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';

const { Option } = Select;
const { RangePicker } = DatePicker;
const suppliersApi = new SuppliersApi();
const projectsApi = new ProjectsApi();
const categoriesApi = new UnitCategoriesApi();

export const SupplierPaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialSupplierId = searchParams.get('supplierId');
  const initialProjectId = searchParams.get('projectId');
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SupplierPaymentDto[]>([]);
  const [query, setQuery] = useState({
    pageNumber: 1,
    pageSize: 10,
    supplierId: initialSupplierId ? Number(initialSupplierId) : undefined,
    projectId: initialProjectId ? Number(initialProjectId) : undefined,
    categoryId: undefined as number | undefined,
    isPaid: undefined as boolean | undefined,
    isPartiallyPaid: undefined as boolean | undefined,
    isDue: undefined as boolean | undefined,
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [categories, setCategories] = useState<UnitCategoryDto[]>([]);
  const [recordPaymentModalVisible, setRecordPaymentModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<SupplierPaymentDto | null>(null);
  const [recordPaymentForm] = Form.useForm();
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<PaymentHistoryEntry[]>([]);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await suppliersApi.apiSuppliersPaymentsGet(query);
      setData(response.data.items || []);
      setPagination({
        ...pagination,
        current: query.pageNumber,
        total: response.data.totalCount,
      });
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await suppliersApi.apiSuppliersGet({ pageSize: -1 });
        setSuppliers(response.data.items || []);

        if (initialSupplierId && !response.data.items?.some(s => s.id === Number(initialSupplierId))) {
          try {
            const supplierResponse = await suppliersApi.apiSuppliersIdGet({
              id: Number(initialSupplierId),
            });
            setSuppliers(prev => [...prev, supplierResponse.data]);
          } catch (error) {
            console.error('Failed to fetch specific supplier:', error);
          }
        }
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
      }
    };
    fetchSuppliers();
  }, [initialSupplierId]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await projectsApi.apiProjectsGet({ pageSize: -1 });
        setProjects(response.data.items || []);

        if (initialProjectId && !response.data.items?.some(p => p.id === Number(initialProjectId))) {
          try {
            const projectResponse = await projectsApi.apiProjectsIdGet({
              id: Number(initialProjectId),
            });
            setProjects(prev => [...prev, projectResponse.data]);
          } catch (error) {
            console.error('Failed to fetch specific project:', error);
          }
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };
    fetchProjects();
  }, [initialProjectId]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (query.projectId) {
        try {
          const response = await categoriesApi.apiUnitCategoriesGet({
            projectId: query.projectId,
            pageSize: -1,
          });
          setCategories(response.data.items || []);
        } catch (error) {
          console.error('Failed to fetch categories:', error);
        }
      } else {
        setCategories([]);
      }
    };
    fetchCategories();
  }, [query.projectId]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (query.supplierId) params.set('supplierId', query.supplierId.toString());
    if (query.projectId) params.set('projectId', query.projectId.toString());
    if (query.categoryId) params.set('categoryId', query.categoryId.toString());
    if (query.isPaid !== undefined) params.set('isPaid', query.isPaid.toString());
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, [query]);

  const handleRecordPayment = async (payment: SupplierPaymentDto) => {
    setSelectedPayment(payment);
    recordPaymentForm.setFieldsValue({
      paidDate: dayjs(),
      paymentMethod: PaymentMethod.Cash, // Default to first payment method
    });
    setRecordPaymentModalVisible(true);
  };

  const handleRecordPaymentSubmit = async () => {
    try {
      setRecordingPayment(true);
      const values = await recordPaymentForm.validateFields();

      await suppliersApi.apiSuppliersPaymentsIdPatch({
        id: selectedPayment!.id!,
        recordSupplierPaymentCommand: {
          paymentId: selectedPayment!.id!,
          supplierId: selectedPayment!.supplierId!,
          ...values,
          paidDate: values.paidDate.toISOString(),
          isFullyPaid: values.isFullyPaid,
          paymentAmount: values.isFullyPaid ? 0 : values.paymentAmount,
        },
      });

      openNotificationWithIcon('success', t('suppliers:payment-recorded', 'Payment recorded successfully'));
      setRecordPaymentModalVisible(false);
      fetchPayments(); // Refresh the table
    } catch (error) {
      openNotificationWithIcon('error', t('suppliers:payment-record-failed', 'Failed to record payment'));
    } finally {
      setRecordingPayment(false);
      recordPaymentForm.resetFields();
    }
  };

  const handleAddPayment = () => {
    navigate('/supplier-payments/create');
  };

  const handleEditPayment = (id: number) => {
    navigate(`/supplier-payments/${id}/edit`);
  };

  const handleDeletePayment = async (id: number) => {
    try {
      await suppliersApi.apiSuppliersPaymentsIdDelete({
        id,
        deleteSupplierPaymentCommand: {
          id,
          supplierId: data.find(p => p.id === id)?.supplierId!,
        },
      });
      openNotificationWithIcon('success', t('suppliers:payment-deleted', 'Payment deleted successfully'));
      fetchPayments(); // Refresh the table
    } catch (error) {
      openNotificationWithIcon('error', t('suppliers:payment-delete-failed', 'Failed to delete payment'));
    }
  };

  // Column definition for desktop view
  const columns = [
    {
      title: t('suppliers:supplier', 'Supplier'),
      dataIndex: 'supplierName',
      key: 'supplierName',
      render: (name: string, record: SupplierPaymentDto) => <Link to={`/suppliers/${record.supplierId}`}>{name}</Link>,
    },
    {
      title: t('suppliers:project', 'Project'),
      dataIndex: 'projectName',
      key: 'projectName',
    },
    {
      title: t('suppliers:total-amount', 'Total Amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: t('suppliers:paid-amount', 'Paid Amount'),
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (paidAmount: number) => formatCurrency(paidAmount),
    },
    {
      title: t('suppliers:description', 'Description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('suppliers:status', 'Status'),
      dataIndex: 'isPaid',
      key: 'isPaid',
      render: (_: any, record: SupplierPaymentDto) => (
        <Tag color={record.isPaid ? 'green' : record.isPartiallyPaid ? 'blue' : record.isOverdue ? 'red' : 'orange'}>
          {record.isPaid
            ? t('suppliers:paid', 'Paid')
            : record.isPartiallyPaid
            ? t('suppliers:partially-paid', 'Partially Paid')
            : record.isOverdue
            ? t('suppliers:overdue', 'Overdue')
            : t('suppliers:pending', 'Pending')}
        </Tag>
      ),
    },
    {
      title: t('suppliers:payment-dates', 'Payment Dates'),
      key: 'paymentDates',
      width: 350,
      render: (_: any, record: SupplierPaymentDto) => (
        <div>
          <div>
            <small>{t('suppliers:due', 'Due')}: </small>
            <span className={record.isOverdue ? 'text-danger' : ''}>{formatDate(record.dueDate)}</span>
          </div>
          {(record.isPaid || record.isPartiallyPaid) && (
            <div>
              {record.isPartiallyPaid && (
                <>
                  <small>{t('suppliers:paid', 'Paid')}: </small>
                  <small>{formatCurrency(record.paidAmount)}</small>
                  <small className="text-secondary">
                    {' '}
                    ({t('suppliers:of', 'of')} {formatCurrency(record.amount)})
                  </small>
                </>
              )}
              {record.paidDate && (
                <div>
                  <small>{t('suppliers:paid', 'Paid')}: </small>
                  <span>{formatDate(record.paidDate)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('suppliers:transaction-history', 'Transaction History'),
      key: 'paymentHistory',
      width: 70,
      render: (record: SupplierPaymentDto) =>
        record.paymentHistoryJson && record.paymentHistoryJson !== '[]' ? (
          <Button
            type="link"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              const history = toCamelCase(JSON.parse(record.paymentHistoryJson || '[]')) as PaymentHistoryEntry[];
              setSelectedHistory(
                history.sort(
                  (a, b) => new Date(b.paymentDate || '').getTime() - new Date(a.paymentDate || '').getTime(),
                ),
              );
              setHistoryModalVisible(true);
            }}
          >
            {JSON.parse(record.paymentHistoryJson || '[]').length === 1
              ? t('suppliers:one-payment', '1 payment')
              : t('suppliers:n-payments', '{{count}} payments', {
                  count: JSON.parse(record.paymentHistoryJson || '[]').length,
                })}
          </Button>
        ) : (
          '-'
        ),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 120,
      render: (record: SupplierPaymentDto) => (
        <div className="table-actions">
          {hasPermission('suppliers:manage-payments') && (
            <Button className="btn-icon" type="primary" shape="circle" onClick={() => handleEditPayment(record.id!)}>
              <FeatherIcon icon="edit" size={16} />
            </Button>
          )}
          {!record.isPaid && hasPermission('suppliers:manage-payments') && (
            <Button className="btn-icon" type="success" shape="circle" onClick={() => handleRecordPayment(record)}>
              <FeatherIcon icon="check" size={16} />
            </Button>
          )}
          {hasPermission('suppliers:manage-payments') && (
            <Popconfirm
              title={t('supplier-payments:delete-confirm', 'Are you sure you want to delete this payment?')}
              onConfirm={() => handleDeletePayment(record.id!)}
              okText={t('global:yes', 'Yes')}
              cancelText={t('global:no', 'No')}
            >
              <Button className="btn-icon" type="danger" shape="circle">
                <FeatherIcon icon="trash-2" size={16} />
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  // Render each payment as a card item for mobile view
  const renderPaymentItem = (item: SupplierPaymentDto) => (
    <Card
      className="payment-card"
      hoverable
      style={{
        marginBottom: '12px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: 'none',
      }}
      bodyStyle={{ padding: '16px' }}
      actions={[
        hasPermission('suppliers:manage-payments') && (
          <FeatherIcon
            icon="edit"
            size={16}
            key="edit"
            onClick={() => handleEditPayment(item.id!)}
            style={{ color: '#1890ff' }}
          />
        ),
        !item.isPaid && hasPermission('suppliers:manage-payments') && (
          <FeatherIcon
            icon="check"
            size={16}
            key="record"
            onClick={() => handleRecordPayment(item)}
            style={{ color: '#52c41a' }}
          />
        ),
        hasPermission('suppliers:manage-payments') && (
          <Popconfirm
            title={t('supplier-payments:delete-confirm', 'Are you sure?')}
            onConfirm={e => {
              e?.stopPropagation();
              handleDeletePayment(item.id!);
            }}
            onCancel={e => {
              e?.stopPropagation();
            }}
            okText={t('global:yes', 'Yes')}
            cancelText={t('global:no', 'No')}
            key="delete"
          >
            <FeatherIcon icon="trash-2" size={16} style={{ color: '#ff4d4f' }} />
          </Popconfirm>
        ),
      ].filter(Boolean)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>{item.description}</div>
          <Link to={`/suppliers/${item.supplierId}`} style={{ color: '#1890ff' }}>
            {item.supplierName}
          </Link>
          {item.projectName && (
            <div style={{ fontSize: '14px', marginTop: '4px' }}>
              <Badge status="processing" text={item.projectName} />
            </div>
          )}
        </div>
        <Tag color={item.isPaid ? 'green' : item.isPartiallyPaid ? 'blue' : item.isOverdue ? 'red' : 'orange'}>
          {item.isPaid
            ? t('suppliers:paid', 'Paid')
            : item.isPartiallyPaid
            ? t('suppliers:partially-paid', 'Partially Paid')
            : item.isOverdue
            ? t('suppliers:overdue', 'Overdue')
            : t('suppliers:pending', 'Pending')}
        </Tag>
      </div>

      <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '10px' }}>{formatCurrency(item.amount)}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px' }}>
        <div>
          <span style={{ color: '#666' }}>{t('suppliers:due', 'Due')}: </span>
          <span className={item.isOverdue && !item.isPaid ? 'text-danger' : ''}>{formatDate(item.dueDate)}</span>
        </div>
        {item.isPaid && (
          <div>
            <span style={{ color: '#666' }}>{t('suppliers:paid', 'Paid')}: </span>
            <span>{formatDate(item.paidDate!)}</span>
          </div>
        )}
      </div>
    </Card>
  );

  // Get title based on filters
  const getPageTitle = () => {
    if (query.supplierId && query.projectId) {
      return (
        <>
          {t('suppliers:payments-for', 'Payments for')}{' '}
          <Link to={`/suppliers/${query.supplierId}`}>{suppliers.find(s => s.id === query.supplierId)?.name}</Link>
          {!isMobile && (
            <>
              {' - '}
              <Link to={`/projects/${query.projectId}`}>{projects.find(p => p.id === query.projectId)?.name}</Link>
            </>
          )}
        </>
      );
    } else if (query.supplierId) {
      return (
        <>
          {t('suppliers:payments-for', 'Payments for')}{' '}
          <Link to={`/suppliers/${query.supplierId}`}>{suppliers.find(s => s.id === query.supplierId)?.name}</Link>
        </>
      );
    } else if (query.projectId) {
      return (
        <>
          {t('suppliers:project-payments', 'Project Payments')}
          {!isMobile && (
            <>
              {' - '}
              <Link to={`/projects/${query.projectId}`}>{projects.find(p => p.id === query.projectId)?.name}</Link>
            </>
          )}
        </>
      );
    } else {
      return t('suppliers:payments', 'Supplier Payments');
    }
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={getPageTitle()}
          buttons={[
            !isMobile && (
              <Button
                key="add"
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddPayment}
                disabled={!hasPermission('suppliers:create')}
              >
                {t('suppliers:add-payment', 'Add Payment')}
              </Button>
            ),
            !isMobile && (
              <ExportButtonPageHeader
                key="export"
                title={t('suppliers:payments', 'Supplier Payments')}
                data={data}
                columns={['supplierName', 'projectName', 'categoryName', 'amount', 'isPaid', 'paymentDate']}
              />
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  {!isMobile && (
                    <>
                      <Col lg={6} md={6} xs={24}>
                        <Select
                          style={{ width: '100%' }}
                          placeholder={t('suppliers:select-supplier', 'Select Supplier')}
                          allowClear
                          value={query.supplierId}
                          onChange={value => setQuery(prev => ({ ...prev, supplierId: value }))}
                        >
                          {suppliers.map(supplier => (
                            <Select.Option key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>

                      <Col lg={6} md={6} xs={24}>
                        <Select
                          style={{ width: '100%' }}
                          placeholder={t('suppliers:select-project', 'Select Project')}
                          allowClear
                          value={query.projectId}
                          onChange={value => {
                            setQuery(prev => ({
                              ...prev,
                              projectId: value,
                              categoryId: undefined, // Clear category when project changes
                            }));
                          }}
                        >
                          {projects.map(project => (
                            <Select.Option key={project.id} value={project.id}>
                              {project.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>

                      <Col lg={6} md={6} xs={24}>
                        <Select
                          style={{ width: '100%' }}
                          placeholder={t('suppliers:payment-status', 'Payment Status')}
                          allowClear
                          value={
                            query.isPaid
                              ? 'paid'
                              : query.isPartiallyPaid
                              ? 'partially-paid'
                              : query.isDue
                              ? 'overdue'
                              : query.isPaid === false
                              ? 'pending'
                              : undefined
                          }
                          onChange={value => {
                            switch (value) {
                              case 'paid':
                                setQuery(prev => ({
                                  ...prev,
                                  isPaid: true,
                                  isPartiallyPaid: undefined,
                                  isDue: undefined,
                                }));
                                break;
                              case 'partially-paid':
                                setQuery(prev => ({
                                  ...prev,
                                  isPartiallyPaid: true,
                                  isPaid: undefined,
                                  isDue: undefined,
                                }));
                                break;
                              case 'pending':
                                setQuery(prev => ({
                                  ...prev,
                                  isPaid: false,
                                  isPartiallyPaid: undefined,
                                  isDue: undefined,
                                }));
                                break;
                              case 'overdue':
                                setQuery(prev => ({
                                  ...prev,
                                  isDue: true,
                                  isPaid: undefined,
                                  isPartiallyPaid: undefined,
                                }));
                                break;
                              default:
                                setQuery(prev => ({
                                  ...prev,
                                  isPaid: undefined,
                                  isPartiallyPaid: undefined,
                                  isDue: undefined,
                                }));
                            }
                          }}
                          getPopupContainer={trigger => trigger.parentElement}
                          dropdownStyle={{ zIndex: 1500 }}
                        >
                          <Select.Option value="paid">{t('suppliers:paid', 'Paid')}</Select.Option>
                          <Select.Option value="partially-paid">
                            {t('suppliers:partially-paid', 'Partially Paid')}
                          </Select.Option>
                          <Select.Option value="pending">{t('suppliers:pending', 'Pending')}</Select.Option>
                          <Select.Option value="overdue">{t('suppliers:overdue', 'Overdue')}</Select.Option>
                        </Select>
                      </Col>
                    </>
                  )}

                  {isMobile && (
                    <>
                      <Col xs={20}>
                        <Select
                          style={{ width: '100%' }}
                          placeholder={t('suppliers:select-supplier', 'Select Supplier')}
                          allowClear
                          value={query.supplierId}
                          onChange={value => setQuery(prev => ({ ...prev, supplierId: value }))}
                        >
                          {suppliers.map(supplier => (
                            <Select.Option key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col xs={4} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Button onClick={() => setFiltersVisible(true)} shape="circle" icon={<FilterOutlined />} />
                      </Col>
                    </>
                  )}
                </Row>
              </TopToolBox>
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              {isMobile ? (
                <List
                  loading={loading}
                  dataSource={data}
                  renderItem={(item, index) => (
                    <div
                      style={{
                        position: 'relative',
                        marginBottom: index === data.length - 1 ? 0 : '12px',
                      }}
                    >
                      {renderPaymentItem(item)}
                      {index < data.length - 1 && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-6px',
                            left: '0',
                            right: '0',
                            height: '6px',
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 100%)',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </div>
                  )}
                  pagination={{
                    current: query.pageNumber,
                    pageSize: query.pageSize,
                    total: pagination.total,
                    onChange: pageNumber => setQuery(prev => ({ ...prev, pageNumber })),
                    simple: true,
                  }}
                  locale={{ emptyText: 'No payments found' }}
                />
              ) : (
                <CustomTableStyleWrapper>
                  <TableWrapper>
                    <Table
                      loading={loading}
                      dataSource={data}
                      columns={columns}
                      pagination={{
                        current: query.pageNumber,
                        pageSize: query.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 50, 100, 1000],
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} ${t('global:of', 'of')} ${total} ${t('global:items', 'items')}`,
                        onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                      }}
                    />
                  </TableWrapper>
                </CustomTableStyleWrapper>
              )}
            </Col>
          </Row>
        </Cards>
      </Main>

      {/* Mobile Filter Drawer */}
      <Drawer
        title={t('global:filters', 'Filters')}
        placement="right"
        onClose={() => setFiltersVisible(false)}
        open={filtersVisible}
        width={isMobile ? '80%' : 450}
      >
        <Form layout="vertical">
          <Form.Item label={t('suppliers:select-project', 'Project')} style={{ width: '100%', marginBottom: '16px' }}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('suppliers:select-project', 'Select Project')}
              allowClear
              value={query.projectId}
              onChange={value => {
                setQuery(prev => ({
                  ...prev,
                  projectId: value,
                  categoryId: undefined,
                }));
              }}
              getPopupContainer={trigger => trigger.parentElement}
              dropdownStyle={{ zIndex: 1500 }}
            >
              {projects.map(project => (
                <Select.Option key={project.id} value={project.id}>
                  {project.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {query.projectId && (
            <Form.Item
              label={t('suppliers:select-category', 'Category')}
              style={{ width: '100%', marginBottom: '16px' }}
            >
              <Select
                style={{ width: '100%' }}
                placeholder={t('suppliers:select-category', 'Select Category')}
                allowClear
                value={query.categoryId}
                onChange={value => setQuery(prev => ({ ...prev, categoryId: value }))}
                getPopupContainer={trigger => trigger.parentElement}
                dropdownStyle={{ zIndex: 1500 }}
              >
                {categories.map(category => (
                  <Select.Option key={category.id} value={category.id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label={t('suppliers:payment-status', 'Payment Status')}
            style={{ width: '100%', marginBottom: '16px' }}
          >
            <Select
              style={{ width: '100%' }}
              placeholder={t('suppliers:payment-status', 'Payment Status')}
              allowClear
              value={
                query.isPaid
                  ? 'paid'
                  : query.isPartiallyPaid
                  ? 'partially-paid'
                  : query.isDue
                  ? 'overdue'
                  : query.isPaid === false
                  ? 'pending'
                  : undefined
              }
              onChange={value => {
                switch (value) {
                  case 'paid':
                    setQuery(prev => ({ ...prev, isPaid: true, isPartiallyPaid: undefined, isDue: undefined }));
                    break;
                  case 'partially-paid':
                    setQuery(prev => ({ ...prev, isPartiallyPaid: true, isPaid: undefined, isDue: undefined }));
                    break;
                  case 'pending':
                    setQuery(prev => ({ ...prev, isPaid: false, isPartiallyPaid: undefined, isDue: undefined }));
                    break;
                  case 'overdue':
                    setQuery(prev => ({ ...prev, isDue: true, isPaid: undefined, isPartiallyPaid: undefined }));
                    break;
                  default:
                    setQuery(prev => ({ ...prev, isPaid: undefined, isPartiallyPaid: undefined, isDue: undefined }));
                }
              }}
              getPopupContainer={trigger => trigger.parentElement}
              dropdownStyle={{ zIndex: 1500 }}
            >
              <Select.Option value="paid">{t('suppliers:paid', 'Paid')}</Select.Option>
              <Select.Option value="partially-paid">{t('suppliers:partially-paid', 'Partially Paid')}</Select.Option>
              <Select.Option value="pending">{t('suppliers:pending', 'Pending')}</Select.Option>
              <Select.Option value="overdue">{t('suppliers:overdue', 'Overdue')}</Select.Option>
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <Button
              onClick={() => {
                setQuery(prev => ({
                  ...prev,
                  projectId: undefined,
                  categoryId: undefined,
                  isPaid: undefined,
                  isPartiallyPaid: undefined,
                  isDue: undefined,
                }));
                setFiltersVisible(false);
              }}
            >
              {t('global:reset', 'Reset')}
            </Button>
            <Button type="primary" onClick={() => setFiltersVisible(false)}>
              {t('global:apply', 'Apply')}
            </Button>
          </div>
        </Form>
      </Drawer>

      {/* Record Payment Modal */}
      <Modal
        title={t('suppliers:record-payment', 'Record Payment')}
        open={recordPaymentModalVisible}
        onCancel={() => setRecordPaymentModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setRecordPaymentModalVisible(false)}>
            {t('global:cancel', 'Cancel')}
          </Button>,
          <Button key="submit" type="primary" loading={recordingPayment} onClick={handleRecordPaymentSubmit}>
            {t('suppliers:record-payment', 'Record Payment')}
          </Button>,
        ]}
      >
        <Form form={recordPaymentForm} layout="vertical" initialValues={{ isFullyPaid: true }}>
          {/* Warning Alert */}
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.isFullyPaid !== curr.isFullyPaid}>
            {({ getFieldValue }) => (
              <Alert
                style={{ marginBottom: '16px' }}
                message={
                  getFieldValue('isFullyPaid')
                    ? t('suppliers:full-payment-warning', 'You are about to record full payment of {{amount}}', {
                        amount: selectedPayment ? formatCurrency(selectedPayment.amount) : '',
                      })
                    : t(
                        'suppliers:partial-payment-warning',
                        'You are about to record a partial payment. Total amount: {{amount}}, Remaining: {{remaining}}',
                        {
                          amount: selectedPayment ? formatCurrency(selectedPayment.amount) : '',
                          remaining: selectedPayment
                            ? formatCurrency(selectedPayment.remainingAmount || selectedPayment.amount)
                            : '',
                        },
                      )
                }
                type="warning"
                showIcon
              />
            )}
          </Form.Item>

          <Form.Item name="isFullyPaid" valuePropName="checked">
            <Checkbox
              onChange={e => {
                const isFullyPaid = e.target.checked;
                if (isFullyPaid) {
                  recordPaymentForm.setFieldValue('paymentAmount', selectedPayment?.amount);
                } else {
                  recordPaymentForm.setFieldValue('paymentAmount', undefined);
                }
              }}
            >
              {t('suppliers:is-fully-paid', 'Pay Full Amount')}
            </Checkbox>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.isFullyPaid !== curr.isFullyPaid}>
            {({ getFieldValue }) =>
              !getFieldValue('isFullyPaid') && (
                <Form.Item
                  name="paymentAmount"
                  label={t('suppliers:payment-amount', 'Payment Amount')}
                  rules={[
                    { required: true, message: t('suppliers:payment-amount-required', 'Please enter payment amount') },
                    {
                      validator: (_, value) => {
                        if (value && selectedPayment && value > (selectedPayment.amount || 0)) {
                          return Promise.reject(
                            t('suppliers:payment-amount-exceeded', 'Payment amount cannot exceed total amount'),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    className="w-full"
                    style={{ width: '100%' }}
                    max={selectedPayment?.remainingAmount}
                    formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => parseFloat(value!.replace(/€\s?|(,*)/g, ''))}
                  />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="paidDate"
            label={t('suppliers:paid-date', 'Paid Date')}
            rules={[{ required: true }]}
            getValueProps={i => ({
              value: i ? dayjs(i) : null,
            })}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              onChange={date => {
                recordPaymentForm.setFieldValue('paidDate', date ? date.format('YYYY-MM-DD') : null);
              }}
            />
          </Form.Item>

          <Form.Item
            name="paymentMethod"
            label={t('suppliers:payment-method', 'Payment Method')}
            rules={[{ required: true }]}
          >
            <Select>
              {Object.entries(PaymentMethod).map(([key, value]) => (
                <Select.Option key={value} value={value}>
                  {t(`payment-methods:${key.toLowerCase()}`, key)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="transactionReference" label={t('suppliers:transaction-reference', 'Transaction Reference')}>
            <Input />
          </Form.Item>

          <Form.Item name="notes" label={t('suppliers:notes', 'Notes')}>
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      {/* Floating action button for mobile */}
      {isMobile && hasPermission('suppliers:create') && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
          }}
        >
          <Button
            type="primary"
            shape="circle"
            size="large"
            onClick={handleAddPayment}
            style={{ width: '60px', height: '60px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}
          >
            <FeatherIcon icon="plus" size={20} />
          </Button>
        </div>
      )}

      <Modal
        title={t('suppliers:payment-history', 'Payment History')}
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)}>
            {t('global:close', 'Close')}
          </Button>,
        ]}
      >
        <List
          dataSource={selectedHistory}
          renderItem={(item, index) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <Space>
                    <span>{formatCurrency(item.amount)}</span>
                    {item.paymentMethod && (
                      <Tag>{t(`payment-methods:${(item.paymentMethod || '').toLowerCase()}`)}</Tag>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <div>{formatDate(item.paymentDate)}</div>
                    {item.transactionReference && (
                      <div>
                        <small>{t('suppliers:transaction-reference', 'Transaction Reference')}:</small>{' '}
                        {item.transactionReference}
                      </div>
                    )}
                    {item.notes && (
                      <div>
                        <small>{t('suppliers:notes', 'Notes')}:</small> {item.notes}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
          locale={{ emptyText: t('suppliers:no-payment-history', 'No payment history available') }}
        />
      </Modal>
    </>
  );
};

export default SupplierPaymentsPage;
